import { useState } from 'react';

interface StatusTimelineProps {
  status: string;
  submittedAt: string;
  reviewedAt?: string;
  notes?: string;
  rejectionReason?: string;
}

const StatusTimeline = ({ status, submittedAt, reviewedAt, notes, rejectionReason }: StatusTimelineProps) => {
  const [showDetails, setShowDetails] = useState(false);

  const getStatusSteps = () => {
    const steps = [
      {
        id: 'submitted',
        title: 'Application Submitted',
        description: 'Your application has been received and is being processed',
        date: submittedAt,
        completed: true,
        current: status === 'submitted'
      },
      {
        id: 'under_review',
        title: 'Under Review',
        description: 'Our team is reviewing your application and documents',
        date: reviewedAt,
        completed: ['under_review', 'approved', 'rejected', 'withdrawn'].includes(status),
        current: status === 'under_review'
      },
      {
        id: 'approved',
        title: 'Application Approved',
        description: 'Congratulations! Your application has been approved',
        date: reviewedAt,
        completed: status === 'approved',
        current: status === 'approved'
      },
      {
        id: 'rejected',
        title: 'Application Rejected',
        description: 'Unfortunately, your application was not approved',
        date: reviewedAt,
        completed: status === 'rejected',
        current: status === 'rejected'
      }
    ];

    return steps.filter(step => step.completed || step.current);
  };

  const steps = getStatusSteps();

  const getStatusColor = (step: any) => {
    if (step.current) {
      return step.id === 'approved' ? 'bg-green-500' : 
             step.id === 'rejected' ? 'bg-red-500' : 'bg-blue-500';
    }
    return 'bg-gray-300';
  };

  const getStatusIcon = (step: any) => {
    if (step.id === 'approved') {
      return (
        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      );
    }
    if (step.id === 'rejected') {
      return (
        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    );
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Application Status Timeline</h3>
        {notes && (
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
          </button>
        )}
      </div>

      <div className="flow-root">
        <ul className="-mb-8">
          {steps.map((step, stepIdx) => (
            <li key={step.id}>
              <div className="relative pb-8">
                {stepIdx !== steps.length - 1 ? (
                  <span
                    className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  />
                ) : null}
                <div className="relative flex space-x-3">
                  <div>
                    <span
                      className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${getStatusColor(step)}`}
                    >
                      {getStatusIcon(step)}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{step.title}</p>
                      <p className="text-sm text-gray-500">{step.description}</p>
                      {step.date && (
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(step.date).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {showDetails && (notes || rejectionReason) && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          {notes && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Admin Notes</h4>
              <p className="text-sm text-gray-700">{notes}</p>
            </div>
          )}
          {rejectionReason && (
            <div>
              <h4 className="text-sm font-medium text-red-900 mb-2">Rejection Reason</h4>
              <p className="text-sm text-red-700">{rejectionReason}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StatusTimeline;
